import type { ReactNode } from "react";
import { ladderMaterial } from "../game/ladders/looks";

/**
 * «مار پله»'s mark (`GAMES[].icon`), the one game mark the app DRAWS instead of typing.
 *
 * CARD-86 ROUND 3, ITEM 4 (the operator, 2026-11-04): «🪜 may show as an empty box on Android 10 and
 * older. Use an icon that shows on every phone.» A Mini App draws emoji with the PHONE's own emoji
 * font, and 🪜 (U+1FA9C) is Emoji 13.0 (2020) — Android 11 was the first to ship it, so every older
 * WebView paints a box. The mark itself stays 🪜 everywhere it is DATA — the catalogue, the start
 * screen's and the end card's badge, the bot's tutorial (`game-stats.test.tsx` holds those
 * together; Telegram draws a bot message's emoji from its own set, so the bot keeps it) — and the
 * web draws it here: the board's own wood ladder (`ladderMaterial(0)`), at the size of the text
 * around it, static.
 */
const LADDERS_MARK = "🪜";

/**
 * A game's mark as the app shows it: «مار پله»'s drawn ({@link LADDERS_MARK}), every other one the
 * very text it always was — the same DOM, so no other game's look changes.
 */
export function GameMark({ icon }: { icon: string }): ReactNode {
  return icon === LADDERS_MARK ? <LadderMark /> : icon;
}

/** The two rails, top to bottom, in a 24-unit box — a little wider at the foot, as a ladder stands. */
const RAILS: readonly (readonly [number, number, number, number])[] = [
  [8, 2.5, 6.2, 21.5],
  [16, 2.5, 17.8, 21.5],
];
/** The rungs' heights; each spans rail to rail at its height. */
const RUNG_Y: readonly number[] = [6.2, 10.6, 15, 19.2];

function railX(rail: readonly [number, number, number, number], y: number): number {
  const [x1, y1, x2, y2] = rail;
  return x1 + ((x2 - x1) * (y - y1)) / (y2 - y1);
}

function LadderMark(): ReactNode {
  const wood = ladderMaterial(0);
  const line = (
    key: string,
    [x1, y1, x2, y2]: readonly [number, number, number, number],
    stroke: string,
    width: number,
  ): ReactNode => (
    <line
      key={key}
      x1={x1}
      y1={y1}
      x2={x2}
      y2={y2}
      stroke={stroke}
      strokeWidth={width}
      strokeLinecap="round"
    />
  );
  const rungs = RUNG_Y.map((y) => [railX(RAILS[0]!, y), y, railX(RAILS[1]!, y), y] as const);
  return (
    <svg
      data-game-mark={LADDERS_MARK}
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      aria-hidden="true"
      focusable="false"
      style={{ display: "inline-block", verticalAlign: "-0.125em" }}
    >
      {rungs.map((r, i) => line(`re${i}`, r, wood.edge, 2.9))}
      {rungs.map((r, i) => line(`r${i}`, r, wood.rung, 1.6))}
      {RAILS.map((r, i) => line(`le${i}`, r, wood.edge, 3.6))}
      {RAILS.map((r, i) => line(`l${i}`, r, wood.rail, 2.2))}
      {RAILS.map((r, i) => line(`lh${i}`, r, wood.railLight, 0.7))}
    </svg>
  );
}
